export interface CitationDto {
  citation_id: string;
  artifact_type: string;
  artifact_id: string;
  document_id?: string | null;
  label?: string | null;
  source_locator?: Record<string, unknown> | null;
  excerpt?: string | null;
}

export interface GroundedAnswerDto {
  answer_text: string;
  citations: CitationDto[];
  uncertainty?: string | null;
  conflicting_evidence?: boolean;
  retrieved_artifacts?: Array<{
    artifact_type: string;
    artifact_id: string;
    document_id?: string | null;
    source_locator?: Record<string, unknown> | null;
  }>;
  knowledge_basis?: 'MACHINE_EVIDENCE' | 'GENERAL_INDUSTRIAL_KNOWLEDGE' | 'CONTEXTUAL_INDUSTRIAL_GUIDANCE' | 'OUT_OF_SCOPE' | string;
  guidance_scope?: 'GENERAL' | 'CONTEXTUAL' | null;
}

export interface ConversationTurnDto {
  turn_id: string;
  conversation_id: string;
  customer_id: string;
  machine_id: string;
  user_id: string;
  question: string;
  answer?: GroundedAnswerDto | null;
  created_at?: string | null;
  interaction_type?: 'ACE_QUESTION' | 'TEAM_UPDATE' | string;
  actor_label?: string | null;
}

export interface ConversationMemoryDto {
  conversation_id: string;
  customer_id: string;
  machine_id: string;
  user_id: string;
  conversation_type: string;
  current_topic?: string | null;
  current_component?: string | null;
  current_document_ids?: string[];
  current_filters?: Record<string, unknown>;
  last_activity_at?: string | null;
}

export interface ConversationDto {
  conversation_id: string;
  customer_id: string;
  machine_id: string;
  user_id: string;
  conversation_type: string;
  title?: string | null;
  status: string;
  turn_count?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CreateConversationResponseDto {
  schema_version: string;
  conversation: ConversationDto;
  conversation_memory: ConversationMemoryDto;
  request_id?: string;
}

export interface ListConversationsResponseDto {
  schema_version: string;
  machine_id: string;
  conversations: ConversationDto[];
  count: number;
  request_id?: string;
}

export interface GetConversationResponseDto {
  schema_version: string;
  conversation: ConversationDto;
  conversation_memory: ConversationMemoryDto | null;
  turns: ConversationTurnDto[];
  turn_count: number;
  next_turn_cursor?: string | null;
  request_id?: string;
}

export interface AskQuestionResponseDto {
  schema_version: string;
  conversation_id: string;
  machine_id: string;
  turn: ConversationTurnDto;
  retrieval: {
    evidence_count: number;
    knowledge_chunks: number;
    facts: number;
    document_summaries: number;
    machine_memory_items: number;
    document_scope_ids?: string[];
    machine_memory_available?: boolean;
  };
  evidence?: {
    strength?: EvidenceStrength;
    citation_count?: number;
    source_document_count?: number;
    conflicting?: boolean;
  };
  request_id?: string;
}

export type EvidenceStrength = 'STRONG' | 'MODERATE' | 'LIMITED' | 'CONFLICTING' | 'INSUFFICIENT';

export function deriveEvidenceStrength(answer: GroundedAnswerDto | null | undefined): EvidenceStrength {
  if (!answer) return 'INSUFFICIENT';
  if (answer.conflicting_evidence) return 'CONFLICTING';
  const citations = answer.citations ?? [];
  const documents = new Set(citations.map((citation) => citation.document_id).filter(Boolean));
  if (citations.length >= 3 && documents.size >= 2) return 'STRONG';
  if (citations.length >= 2) return 'MODERATE';
  if (citations.length >= 1) return 'LIMITED';
  return 'INSUFFICIENT';
}

export function conversationTitle(question: string): string {
  const clean = question.replace(/\s+/g, ' ').trim();
  if (clean.length <= 72) return clean;
  return `${clean.slice(0, 69).trimEnd()}…`;
}
